import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { Identification } from '@jane/shared/models';

import { NotificationsService } from '../../services/notifications';
import { IdentificationSource } from '../../sources/identification';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { UPDATE_USERS } from './users';

export const UPDATE_IDENTIFICATION = 'identification/update';
const initiateUpdateIdentification = createAction<
  void,
  typeof UPDATE_IDENTIFICATION
>(UPDATE_IDENTIFICATION);

export const UPDATE_IDENTIFICATION_SUCCESS = 'identification/update-success';
export const updateIdentificationSuccess = createAction<
  { first_name: string; last_name: string },
  typeof UPDATE_IDENTIFICATION_SUCCESS
>(UPDATE_IDENTIFICATION_SUCCESS);

export const UPDATE_IDENTIFICATION_ERROR = 'identification/update-error';
export const updateIdentificationError = createAction<
  void,
  typeof UPDATE_IDENTIFICATION_ERROR
>(UPDATE_IDENTIFICATION_ERROR);

export const updateIdentification =
  (
    documentPart: Partial<Identification>,
    onSuccess?: () => void
  ): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateUpdateIdentification());

    IdentificationSource.update({ document: documentPart }).then(
      ({ document, message }) => {
        const documentWithImmediateImages = {
          ...document,
          ...documentPart,
        };

        NotificationsService.success(message);
        dispatch(updateIdentificationSuccess(documentWithImmediateImages));
        onSuccess && onSuccess();
      },
      () => {
        dispatch(updateIdentificationError());
      }
    );
  };

const GET_IDENTIFICATION = 'identification/get-identification';
const getIdentificationSuccess = createAction<
  { document: Identification },
  typeof GET_IDENTIFICATION
>(GET_IDENTIFICATION);

export const getIdentification = (): CustomerThunkAction => (dispatch) =>
  IdentificationSource.get().then((result) =>
    dispatch(getIdentificationSuccess(result))
  );

export const UPLOAD_IDENTIFICATION = 'identification/upload';
const initiateUploadIdentification = createAction<
  string,
  typeof UPLOAD_IDENTIFICATION
>(UPLOAD_IDENTIFICATION);

const UPLOAD_IDENTIFICATION_SUCCESS = 'identification/upload-success';
export const uploadIdentificationSuccess = createAction<
  { key: keyof Identification; value: string | null },
  typeof UPLOAD_IDENTIFICATION_SUCCESS
>(UPLOAD_IDENTIFICATION_SUCCESS);

const UPLOAD_IDENTIFICATION_ERROR = 'identification/upload-error';
export const uploadIdentificationError = createAction<
  unknown,
  typeof UPLOAD_IDENTIFICATION_ERROR
>(UPLOAD_IDENTIFICATION_ERROR);

export const uploadIdentification =
  ({
    key,
    value,
  }: {
    key: keyof Identification;
    value: string | null;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateUploadIdentification(key));

    const data = {
      document: {
        [key]: value,
      },
    };

    IdentificationSource.update(data).then(
      () => dispatch(uploadIdentificationSuccess({ key, value })),
      () => dispatch(uploadIdentificationError(key))
    );
  };

const UPDATE_BIRTH_DATE = 'identification/update-birth-date';
export const updateBirthDate = createAction<string, typeof UPDATE_BIRTH_DATE>(
  UPDATE_BIRTH_DATE
);

const SET_IDENTIFICATION_VALUE = 'identification/set-identification-value';
export const setIdentificationValue = createAction<
  { name: keyof Identification; value: string },
  typeof SET_IDENTIFICATION_VALUE
>(SET_IDENTIFICATION_VALUE);

export type IdentificationActions =
  | ReturnType<typeof initiateUpdateIdentification>
  | ReturnType<typeof updateIdentificationError>
  | ReturnType<typeof updateIdentificationSuccess>
  | ReturnType<typeof getIdentificationSuccess>
  | ReturnType<typeof initiateUploadIdentification>
  | ReturnType<typeof uploadIdentificationSuccess>
  | ReturnType<typeof uploadIdentificationError>
  | ReturnType<typeof updateBirthDate>
  | ReturnType<typeof setIdentificationValue>;

interface Checkers {
  birth_date: boolean;
  first_name: boolean;
  government_photo: boolean;
  last_name: boolean;
  mmj_card: boolean;
  student_photo: boolean;
  veteran_photo: boolean;
}

export type IdentificationState = DeepReadonly<{
  checkers: Checkers;
  document: Identification;
  documentHasLoaded: boolean;
  isUploading: boolean;
  loading_government_photo: boolean;
  loading_mmj_card: boolean;
  loading_mmj_card_back: boolean;
  loading_student_photo: boolean;
  loading_veteran_photo: boolean;
}>;

const getInitialState = (): IdentificationState => ({
  checkers: {
    first_name: false,
    last_name: false,
    birth_date: false,
    government_photo: false,
    mmj_card: false,
    student_photo: false,
    veteran_photo: false,
  },
  document: {
    first_name: '',
    last_name: '',
    birth_date: '',
    government_photo: null,
    mmj_card: null,
    mmj_card_back: null,
    student_photo: null,
    veteran_photo: null,
  },
  documentHasLoaded: false,
  isUploading: false,
  loading_government_photo: false,
  loading_mmj_card: false,
  loading_mmj_card_back: false,
  loading_student_photo: false,
  loading_veteran_photo: false,
});

const getCheckers = (document: Identification): Checkers => ({
  first_name: !!document.first_name,
  last_name: !!document.last_name,
  birth_date: !!document.birth_date,
  government_photo: !!document.government_photo,
  mmj_card: !!(document.mmj_card && document.mmj_card_back),
  student_photo: !!document.student_photo,
  veteran_photo: !!document.veteran_photo,
});

const setValue = (
  state: IdentificationState,
  key: keyof Identification,
  value: string | number | null
) => {
  const document = {
    ...state.document,
    [key]: value,
  };
  return { ...state, document, checkers: getCheckers(document) };
};
export const identificationReducer: Reducer<
  IdentificationState,
  CustomerAction
> = (state = getInitialState(), action) => {
  switch (action.type) {
    case GET_IDENTIFICATION: {
      const document = { ...state.document, ...action.payload.document };
      return {
        ...state,
        document,
        checkers: getCheckers(document),
        documentHasLoaded: true,
      };
    }

    case SET_IDENTIFICATION_VALUE: {
      return setValue(state, action.payload.name, action.payload.value);
    }

    case UPDATE_IDENTIFICATION:
      return { ...state, isUploading: true };

    case UPDATE_IDENTIFICATION_SUCCESS:
      return {
        ...state,
        isUploading: false,
        document: {
          ...state.document,
          ...action.payload,
        },
        checkers: getCheckers(action.payload),
      };

    case UPDATE_IDENTIFICATION_ERROR:
      return { ...state, isUploading: false };

    case UPLOAD_IDENTIFICATION:
      return { ...state, [`loading_${action.payload}`]: true };

    case UPLOAD_IDENTIFICATION_SUCCESS:
      return setValue(
        {
          ...state,
          [`loading_${action.payload.key}`]: false,
        },
        action.payload.key,
        action.payload.value
      );

    case UPLOAD_IDENTIFICATION_ERROR:
      return { ...state, [`loading_${action.payload}`]: false };

    case UPDATE_BIRTH_DATE:
      return setValue(state, 'birth_date', action.payload);

    case UPDATE_USERS:
      return {
        ...state,
        isUploading: false,
        document: {
          ...state.document,
          first_name: action.payload.first_name,
          last_name: action.payload.last_name,
        },
      };
  }

  return state;
};

export const selectIdentification = (state: AppState) => state.identification;
